import {
  billingProcedures,
  bindProcedures,
  monthlyWhenYearly,
  SEED_PLANS,
  type BillingFamilyClient,
} from '@pointfive/contracts';
import { paginate, type SimRuntime } from '../runtime';

/** The sim serves the same catalogue the seed writes and the screen renders — one definition, in contracts. A second
 *  hand-written list here would drift from the real prices the moment either changed. */
const PLANS = SEED_PLANS.map((plan) => ({
  id: `plan_${plan.key}`,
  tier: plan.tier,
  name: plan.name,
  priceMonthly: plan.monthly,
  priceMonthlyYearly: monthlyWhenYearly(plan.monthly),
  currency: 'USD',
  blurb: plan.blurb,
  highlights: [...plan.highlights],
  inherits: plan.inherits,
  recommended: plan.recommended,
  features: [...plan.features],
  limits: {
    accountSlots: plan.accountSlots,
    aiDailyBudget: plan.aiDailyBudget,
    voiceMinutes: plan.voiceMinutes,
  },
}));

/** billing family sim binding (`manage.billing.*`): plans, hosted checkout hand-off, invoices. Nothing is granted locally —
 *  a checkout stays `pending_webhook` until a (simulated) webhook lands; Phase 1 never flips it. */
export function bindBilling(rt: SimRuntime): BillingFamilyClient {
  let subscription: {
    planId: string;
    tier: 'free' | 'starter' | 'trader' | 'pro';
    status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'pending_webhook';
    interval: 'monthly' | 'yearly';
    renewsAt: string | null;
    discountCode: string | null;
  } = {
    planId: 'plan_trader',
    tier: 'trader',
    status: 'active',
    interval: 'monthly',
    renewsAt: '2026-08-12T00:00:00.000Z',
    discountCode: null,
  };
  const invoices = [0, 1, 2, 3].map((i) => ({
    id: `inv_${String(i + 1).padStart(6, '0')}`,
    number: `PF-2026-${String(1040 + i)}`,
    amount: 79,
    currency: 'USD',
    status: 'paid' as const,
    issuedAt: `2026-0${4 + i}-12T00:00:00.000Z`,
    pdfUrl: null,
  }));
  const checkouts = new Map<
    string,
    {
      checkoutId: string;
      url: string;
      state: 'created' | 'pending_webhook' | 'completed' | 'expired';
      discountReserved: boolean;
    }
  >();
  const current = () => PLANS.find((p) => p.id === subscription.planId) ?? PLANS[0]!;
  return bindProcedures(billingProcedures, {
    'manage.billing.plans': () => {
      const cur = current();
      return {
        meta: rt.meta(),
        plans: PLANS.map((p) => ({
          ...p,
          features: [...p.features],
          limits: { ...p.limits },
          current: p.id === subscription.planId,
        })),
        subscription: { ...subscription },
        diff: Object.fromEntries(
          PLANS.map((p) => [
            p.id,
            {
              gained: p.features.filter((f) => !cur.features.includes(f)),
              lost: cur.features.filter((f) => !p.features.includes(f)),
            },
          ]),
        ),
      };
    },
    'manage.billing.checkout': ({ idempotencyKey, planId, discountCode }) => {
      const prev = checkouts.get(idempotencyKey);
      if (prev) return { meta: rt.meta(), ...prev };
      if (!PLANS.some((p) => p.id === planId))
        rt.fail({
          code: 'VALIDATION',
          message: 'Unknown plan',
          issues: [{ path: 'planId', message: 'unknown plan' }],
        });
      if (planId === subscription.planId)
        rt.fail({ code: 'CONFLICT', message: 'You are already on this plan.' });
      rt.clock.tick();
      const co = {
        checkoutId: rt.id('chk'),
        url: `https://checkout.simulated.local/c/${rt.id('cs')}`,
        state: 'pending_webhook' as const,
        discountReserved: !!discountCode,
      };
      checkouts.set(idempotencyKey, co);
      subscription = { ...subscription, status: 'pending_webhook' };
      return { meta: rt.meta(), ...co };
    },
    'manage.billing.portal': () => ({ meta: rt.meta(), url: 'https://billing.simulated.local/portal' }),
    'manage.billing.invoices': (input) => paginate(invoices, input, rt.meta()),
    'manage.billing.subscription': () => ({ meta: rt.meta(), subscription: { ...subscription } }),
  });
}
